import {
  ChangeDetectionStrategy, ChangeDetectorRef, Component, computed,
  inject, model, OnDestroy, OnInit, signal,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Subject, takeUntil } from 'rxjs';
import { FikoYTKokBilesenComponent } from '@service/kok-bilesen/kok-bilesen.component';
import { KartWebSocketService } from '../services/kart-websocket.service';
import { FikoYTIstekKok } from '@/src/service/modules/fikoyt-istek-kok/fikoyt.istek.makinesi.service';
import { AvatarService } from '@/src/app/servisler/avatar.service';

/**
 * Paylaşım sistemi edit görünümü (paylas_kutusu) —
 * Bir kart okutulur, okutulan kartın UID'si yakalanır ve bu karta
 * (kime ait olduğunu belirten) bir isim atanır. Takip, personel/firma
 * kaydı gerektirmeden doğrudan bu tek kart UID'si üzerinden yapılır.
 */
@Component({
  selector: 'kart-sahibi-paylasim-edit',
  standalone: true,
  imports: [CommonModule, FormsModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './kart-sahibi-edit.component.html',
  styleUrl: './kart-sahibi-edit.component.less',
})
export class KartSahibiEditComponent extends FikoYTKokBilesenComponent implements OnInit, OnDestroy {
  parametreler = model<any>();

  baglimi = signal(false);
  kartUid = signal('');
  resimYukleniyor = signal(false);

  /** Ayni resim id'si yeniden yuklendiginde tarayici onbellegini atlatmak icin
   *  arttirilan sayac — resimSecildi() basariyla bittiginde guncellenir. */
  private _resimCacheBust = signal(0);

  private _destroy$ = new Subject<void>();
  private wsService = inject(KartWebSocketService);
  private cd = inject(ChangeDetectorRef);
  private avatar = inject(AvatarService);

  constructor() { super(); }

  ngOnInit() {
    // Mevcut parametrelerden önceden okutulmuş kartı geri yükle
    // (gerçek alanlar üst seviyede değil, parametreler().ek içinde tutulur —
    //  bkz. IKartComponent.dinamikParametreleriGuncelle: { kart, icerik, ek })
    const ek = this.parametreler?.()?.ek ?? {};
    if (ek.kartUid) {
      this.kartUid.set(ek.kartUid);
    }

    this.wsService.durum$
      .pipe(takeUntil(this._destroy$))
      .subscribe(durum => {
        this.baglimi.set(durum === 'bagli');
        this.cd.markForCheck();
      });

    // Kart okutulduğunda UID'yi yakala — sadece henüz bir kart atanmamışsa
    // otomatik yakala; "Değiştir" ile yeniden dinlemeye açılır.
    this.wsService.kartOlayi$
      .pipe(takeUntil(this._destroy$))
      .subscribe(olay => {
        if (olay.durum !== 'koyuldu' || !olay.kartKimlik) return;
        if (this.kartUid() && this.kartUid() !== '') return;
        this.kartUidAta(olay.kartKimlik);
      });

    if (!this.wsService.bagli) {
      this.wsService.baglan();
    }
  }

  private kartUidAta(uid: string) {
    this.kartUid.set(uid);
    this.ekGuncelle('kartUid', uid);
  }

  /** "Değiştir" — yeni bir kart okutulana kadar mevcut UID'yi geçici olarak temizler */
  yenidenOkut() {
    this.kartUid.set('');
    this.cd.markForCheck();
  }

  guncelle(alan: string, deger: string) {
    this.ekGuncelle(alan, deger);
  }

  get sahipResimId(): string {
    return this.parametreler()?.ek?.sahipResim || '';
  }
  /** parametreler() (id degisimi) veya _resimCacheBust (ayni id yeniden yuklendi)
   *  degistiginde otomatik yeniden hesaplanir; OnPush altinda [src] ve @if
   *  buna gore guncellenir. */
  sahipResimUrl = computed(() => {
    const id = this.parametreler()?.ek?.sahipResim || '';
    const bust = this._resimCacheBust();
    return id ? this.avatar.profilResimUrl(id, '128x128') + (bust ? `&_t=${bust}` : '') : '';
  });

  /** Profil resmi ekle/değiştir — 'FikoYT.sistem.dosya.:db.yukle' genel yükleme
   *  endpoint'i + FormData kullanır (bkz. sistem/kokDB/routes.go, fikoyt_sistem_dosya_yukle).
   *  NOT: PdksKisilerService.resimYukle hâlâ var olmayan 'FikoYT.sistem.dosyalar.yukle'
   *  komutunu kullanıyor — aynı hatayı taşıyor, ayrıca düzeltilmesi gerekiyor. */
  async resimSecildi(event: any) {
    const dosya: File = event?.target?.files?.[0];
    if (!dosya) return;
    if (!dosya.type.startsWith('image')) {
      event.target.value = '';
      return;
    }

    this.resimYukleniyor.set(true);
    this.cd.markForCheck();
    try {
      // NOT: 'FikoYT.sistem.dosyalar.yukle' diye bir backend route yok (bkz. routes.go) —
      // gerçek genel-amaçlı yükleme endpoint'i 'FikoYT.sistem.dosya.:db.yukle' (kok DB, tekil
      // "dosya"). ÖNEMLİ: ':db' parametresi sadece metadata etiketi değil — saveToGridFS()
      // bunu GERÇEK GridFS bucket adı olarak kullanıyor (bkz. FikoYTDosya/fikoyt_dosya.go,
      // saveToGridFS: metaData["koleksiyon"] varsa fsName = koleksiyon). Ama resmi okuyan
      // dosyalar.:id.resim route'u HER ZAMAN sabit "go_files" bucket'ından okuyor. Bu yuzden
      // ':db' MUTLAKA 'go_files' olmali, yoksa dosya farkli bir bucket'a yazilip okunamaz
      // (sunucu "resim yok" placeholder'ini doner — yasadigimiz bug tam olarak buydu).
      const istek = new FikoYTIstekKok({ metod: 'POST', komut: 'FikoYT.sistem.dosya.go_files.yukle' });
      const fd = new FormData();
      fd.append('file', dosya);
      // NOT: fikoyt_json.AraIsleyici() FikoYT.Istek.Json'ı 'req_body' form alanından
      // dolduruyor; bu alan yoksa Json nil kalıyor ve backend nil map'e yazarken
      // ("metadata['koleksiyon'] = ...") panic atıp 500 dönüyordu. Boş JSON metadata
      // ile bunu çözüyoruz.
      fd.append('req_body', JSON.stringify({}));
      istek.parametreler = fd;
      await istek.isteSync();
      const id: string | null = istek.cevap?.sonuc?.sonuclar?.[0]?.id || null;
      if (id) {
        // NOT: dosya.yukle cevabi anlik degil — dosya arka planda (ffmpeg sikistirma +
        // GridFS yazimi) islenip kaydediliyor (bkz. fikoyt_sistem_dosya_yukle). Bu yuzden
        // id'yi hemen avatara baglarsak GridFS'te henuz olmayan bir dosyayi istemis oluyoruz
        // ve backend "resim yok" placeholder'ini donuyor. dosya.bilgi ile hazir olana kadar
        // bekliyoruz (bkz. fikoyt_sistem_dosya_bilgi, durum:"isleniyor" -> hazir olunca degisir).
        await this.dosyaHazirOlanaKadarBekle(id);
        this.ekGuncelle('sahipResim', id);
        // Ayni id ile yeniden yuklenmis olabilir (kullanici ayni dosyayi tekrar
        // sectiyse parametreler() degismez) — cache-bust sayacini her durumda ilerlet.
        this._resimCacheBust.set(Date.now());
      }
    } catch (err) {
      console.error('[KartSahibiEdit] profil resmi yüklenemedi', err);
    } finally {
      this.resimYukleniyor.set(false);
      event.target.value = '';
      this.cd.markForCheck();
    }
  }

  resimKaldir() {
    this.ekGuncelle('sahipResim', '');
  }

  /** dosya.yukle arka planda islendigi icin (bkz. resimSecildi yorum) dosya.bilgi
   *  endpoint'ini polling ile hazir olana kadar bekler. durum:"isleniyor" donerse
   *  tekrar dener; bulunamazsa/hazir olursa (veya deneme hakki biterse) doner. */
  private async dosyaHazirOlanaKadarBekle(id: string, maxDeneme = 20, araGecikmeMs = 500): Promise<void> {
    for (let i = 0; i < maxDeneme; i++) {
      try {
        const bilgiIstek = new FikoYTIstekKok({ metod: 'GET', komut: 'FikoYT.sistem.dosya.bilgi/' + id });
        // NOT: constructor 'parametreler'i varsayilan olarak {} yapiyor ve bu HttpRequest'e
        // body olarak geciyor. Tarayici GET/HEAD isteklerinde body'e izin vermiyor
        // ("Request with GET/HEAD method cannot have body") — bu yuzden body'i temizliyoruz.
        bilgiIstek.parametreler = undefined;
        await bilgiIstek.isteSync();
        if (bilgiIstek.cevap?.sonuc?.durum !== 'isleniyor') {
          return;
        }
      } catch {
        // istek basarisiz olsa bile devam et, tekrar dene
      }
      await new Promise(r => setTimeout(r, araGecikmeMs));
    }
  }

  /**
   * Gerçek alanları parametreler().ek içine yazar. `ek`, kart içeriğinin
   * (icerik.ek) doğrudan referansı olduğu için burada mutasyon yapmak,
   * kartın kaydedilen verisine de aynen yansır. Üstte yeni bir sarmalayıcı
   * obje oluşturmak ise sadece Angular reaktivitesini tetiklemek içindir.
   */
  private ekGuncelle(alan: string, deger: any) {
    const params = this.parametreler() ?? {};
    const ek = params.ek ?? {};
    ek[alan] = deger;
    this.parametreler.set({ ...params, ek });
    this.cd.markForCheck();
  }

  ngOnDestroy() {
    super.ngOnDestroy();
    this._destroy$.next();
    this._destroy$.complete();
  }
}



